/**
 * useStayCatchUp — «Posar al dia» (`plans/fitxes/20261012-posar-al-dia.md`).
 *
 * `GET /stay-catch-up` (què hi ha per marcar de l'última setmana), `POST /stay-catch-up/apply`
 * (marcar-ho de cop) i `POST /stay-catch-up/undo` (desfer el que aquella crida va canviar). Les tres
 * rutes només escriuen a la DB: res a Beds24, cap codi de porta.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  StayCatchUpApplyResponse,
  StayCatchUpItem,
  StayCatchUpResponse,
  StayCatchUpUndoItem,
  StayCatchUpUndoResponse,
} from '@guilleumes/types';
import { api } from '@/services/api';

export const STAY_CATCH_UP_KEY = ['stay-catch-up'] as const;

export async function fetchStayCatchUp(): Promise<StayCatchUpResponse> {
  const response = await api.get<StayCatchUpResponse>('/stay-catch-up');
  return response.data;
}

export async function postStayCatchUpApply(items: StayCatchUpItem[]): Promise<StayCatchUpApplyResponse> {
  const response = await api.post<StayCatchUpApplyResponse>('/stay-catch-up/apply', { items });
  return response.data;
}

export async function postStayCatchUpUndo(items: StayCatchUpUndoItem[]): Promise<StayCatchUpUndoResponse> {
  const response = await api.post<StayCatchUpUndoResponse>('/stay-catch-up/undo', { items });
  return response.data;
}

/** El que canvia amb una entrada, una sortida o una neteja: el calendari, la fitxa i aquesta taula. */
export function invalidateAfterCatchUp(queryClient: Pick<QueryClient, 'invalidateQueries'>): void {
  queryClient.invalidateQueries({ queryKey: ['calendar-bookings'], exact: false });
  queryClient.invalidateQueries({ queryKey: ['reservation-full'], exact: false });
  queryClient.invalidateQueries({ queryKey: STAY_CATCH_UP_KEY });
}

const errorDetail = (error: unknown): string =>
  error instanceof Error && error.message ? `: ${error.message}` : '';

export function useStayCatchUp(enabled = true) {
  return useQuery({
    queryKey: STAY_CATCH_UP_KEY,
    queryFn: fetchStayCatchUp,
    enabled,
    staleTime: 60_000,
  });
}

export function useStayCatchUpApply() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postStayCatchUpApply,
    onSuccess: () => invalidateAfterCatchUp(queryClient),
    onError: (error: unknown) => {
      toast.error(`No s'ha pogut posar al dia${errorDetail(error)}`);
    },
  });
}

export function useStayCatchUpUndo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postStayCatchUpUndo,
    onSuccess: () => invalidateAfterCatchUp(queryClient),
    onError: (error: unknown) => {
      toast.error(`No s'ha pogut desfer${errorDetail(error)}`);
    },
  });
}
